import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import gymService, { GymDetails, GymAnalytics, GymCreationData, GymUpdateData } from '../services/gymService';

interface GymState {
  gyms: GymDetails[];
  selectedGym: GymDetails | null;
  analytics: GymAnalytics | null;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
}

const initialState: GymState = {
  gyms: [],
  selectedGym: null,
  analytics: null,
  isLoading: false,
  error: null,
  totalCount: 0
};

/**
 * Async thunk for creating gym
 */
export const createGym = createAsyncThunk(
  'gym/create',
  async (data: GymCreationData, { rejectWithValue }) => {
    try {
      const response = await gymService.createGym(data);
      return response.gym;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create gym');
    }
  }
);

/**
 * Async thunk for fetching all gyms
 */
export const fetchPartnerGyms = createAsyncThunk(
  'gym/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await gymService.getPartnerGyms();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch gyms');
    }
  }
);

/**
 * Async thunk for fetching single gym
 */
export const fetchGymDetails = createAsyncThunk(
  'gym/fetchDetails',
  async (gymId: number, { rejectWithValue }) => {
    try {
      const gym = await gymService.getGymDetails(gymId);
      return gym;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch gym details');
    }
  }
);

/**
 * Async thunk for updating gym
 */
export const updateGym = createAsyncThunk(
  'gym/update',
  async ({ gymId, data }: { gymId: number; data: GymUpdateData }, { rejectWithValue }) => {
    try {
      const response = await gymService.updateGym(gymId, data);
      return response.gym;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update gym');
    }
  }
);

/**
 * Async thunk for deleting gym
 */
export const deleteGym = createAsyncThunk(
  'gym/delete',
  async (gymId: number, { rejectWithValue }) => {
    try {
      await gymService.deleteGym(gymId);
      return gymId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete gym');
    }
  }
);

/**
 * Async thunk for fetching gym analytics
 */
export const fetchGymAnalytics = createAsyncThunk(
  'gym/fetchAnalytics',
  async ({ gymId, startDate, endDate }: { gymId: number; startDate?: string; endDate?: string }, { rejectWithValue }) => {
    try {
      const analytics = await gymService.getGymAnalytics(gymId, startDate, endDate);
      return analytics;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics');
    }
  }
);

const gymSlice = createSlice({
  name: 'gym',
  initialState,
  reducers: {
    clearSelectedGym: (state) => {
      state.selectedGym = null;
      state.analytics = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Create Gym
    builder
      .addCase(createGym.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createGym.fulfilled, (state, action) => {
        state.isLoading = false;
        state.gyms.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(createGym.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch All Gyms
    builder
      .addCase(fetchPartnerGyms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPartnerGyms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.gyms = action.payload.gyms;
        state.totalCount = action.payload.total_count;
      })
      .addCase(fetchPartnerGyms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Gym Details
    builder
      .addCase(fetchGymDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGymDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedGym = action.payload;
      })
      .addCase(fetchGymDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Gym
    builder
      .addCase(updateGym.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateGym.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.gyms.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.gyms[index] = action.payload;
        }
        if (state.selectedGym?.id === action.payload.id) {
          state.selectedGym = action.payload;
        }
      })
      .addCase(updateGym.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete Gym
    builder
      .addCase(deleteGym.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteGym.fulfilled, (state, action) => {
        state.isLoading = false;
        state.gyms = state.gyms.filter((g) => g.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedGym?.id === action.payload) {
          state.selectedGym = null;
        }
      })
      .addCase(deleteGym.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Analytics
    builder
      .addCase(fetchGymAnalytics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGymAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchGymAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearSelectedGym, clearError } = gymSlice.actions;
export default gymSlice.reducer;
